import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const [isAdded, setIsAdded] = useState(false);

  const handleAdd = () => {
    addToCart(product);
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 2000);
  };

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -5 }}
      className="dashboard-card group flex flex-col h-full bg-white shadow-sm hover:shadow-xl hover:shadow-indigo-500/5"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-50 border-b border-slate-100">
        <picture>
          <source 
            type="image/avif" 
            srcSet={`${product.image}&fm=avif&w=400 400w, ${product.image}&fm=avif&w=800 800w`} 
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
          />
          <source 
            type="image/webp" 
            srcSet={`${product.image}&fm=webp&w=400 400w, ${product.image}&fm=webp&w=800 800w`} 
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
          />
          <img 
            src={product.image} 
            alt={`Product view of ${product.name} showing its ${product.specs[0]} features`} 
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            loading="lazy"
            decoding="async"
            width="400"
            height="300"
          />
        </picture>
        <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/5 transition-colors duration-300"></div>
        
        {product.tag && (
          <div className="absolute top-3 left-3">
            <span className="bg-slate-900 text-white text-[9px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest flex items-center gap-1.5 shadow-lg shadow-black/20">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
              {product.tag}
            </span>
          </div>
        )}
        
        <button 
          onClick={handleAdd}
          disabled={isAdded}
          className={`absolute bottom-3 right-3 w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 transform group-hover:translate-y-0 ${
            isAdded 
              ? 'bg-emerald-500 text-white scale-110' 
              : 'bg-white/90 backdrop-blur text-slate-800 opacity-0 group-hover:opacity-100 translate-y-2 hover:bg-indigo-600 hover:text-white'
          }`}
          aria-label={isAdded ? "Added to cart" : `Add ${product.name} to cart`}
        >
          <SafeIcon icon={isAdded ? FiIcons.FiCheck : FiIcons.FiPlus} className="text-lg" />
        </button>
      </div>
      
      <div className="p-5 flex flex-col flex-grow">
        <div className="flex items-center justify-between mb-2">
          <span className="mono-label text-indigo-600 text-[9px]">{product.category}</span>
          <div className="flex gap-0.5">
            {[1,2,3,4,5].map(i => (
              <SafeIcon key={i} icon={FiIcons.FiStar} className="text-[10px] text-amber-400 fill-amber-400" />
            ))}
          </div>
        </div>
        
        <h3 className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors mb-3 line-clamp-1">
          {product.name}
        </h3>
        
        <div className="space-y-2 mb-6 flex-grow">
          {product.specs.map((spec, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
              <SafeIcon icon={FiIcons.FiChevronRight} className="text-indigo-400 text-[10px]" />
              {spec}
            </div>
          ))}
        </div>
        
        <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
          <div>
            <span className="text-[10px] text-slate-400 font-mono block leading-none mb-1 uppercase tracking-tighter">Investment</span>
            <span className="text-xl font-bold text-slate-900 font-mono">${product.price}</span>
          </div>
          <button className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 group/btn focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded px-1">
            Details <SafeIcon icon={FiIcons.FiArrowUpRight} className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;